<template>
  <div>
    <!-- page meta -->
    <Head>
      <title>Sell Courses Online with Platforma</title>
      <link
        rel="icon"
        :href="$filters.public_path('favicon.png')"
        type="favicon"
      />
      <meta
        name="description"
        content="Make money online by selling advanced courses that will wow your students."
      />
    </Head>
  </div>
  <!-- content start -->

  <div
    class="min-[1140px]:max-w-[1140px] container relative mx-auto bg-transparent"
  >
    <!-- ===================== HERO SECTION ===================== -->

    <section
      id="section1"
      class="hero-section relative mx-auto mt-[150px] justify-center gap-1 bg-transparent lg:flex lg:py-0"
      :style="backgroundImageStyleMain"
    >
      <div class="mt-0 bg-transparent px-[25px] xl:px-0">
        <h1
          class="top-full mt-0 px-0 text-[35px] font-extrabold tracking-normal text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
        >
          Get More ROI<br />
          with Platforma Courses
        </h1>

        <p
          class="mt-[5px] mb-[25px] text-[15px] font-medium text-[#7c828d] lg:text-lg"
        >
          Construct, host, and sell your course with powerful program building
          management that can be customized for every need.
        </p>

        <!-- captcher email widget -->
        <EmailCaptureHeaderWidget />

        <img :src="$filters.media_path('/images/stars.svg')" alt="" />
      </div>

      <div class="mb-[30px] mt-[30px] lg:mt-0 z-10">
        <img
          loading="lazy"
          class="lg:max-w-[650px]"
          :src="
            $filters.media_path(
              '/images/build-an-amazing-online-course-to-make-money-online.png'
            )
          "
          alt="Easiy monetize your knowledge with advanced courses."
        />
      </div>
    </section>

    <div
      class="join-mobile mx-auto mt-[30px] h-[150px] max-w-6xl gap-2 bg-[#f5f8fa] py-[65px] md:mt-[150px] md:flex md:h-auto md:bg-[#fafbfc] md:bg-blend-overlay"
    >
      <div class="m-auto hidden h-[1px] w-[400px] bg-[#e9ebf0] md:flex"></div>
      <h2
        class="mx-auto px-0 text-center text-[12px] font-[500] text-[#7c828d]"
      >
        JOIN 10,000+ HIGH EARNING BUSINESSES & CREATORS
      </h2>
      <div class="m-auto hidden h-[1px] w-[400px] bg-[#e9ebf0] md:flex"></div>
    </div>

    <!-- ===================== VIDEO HOSTING ===================== -->
    <section
      class="relative mt-[70px] w-full justify-center gap-2 px-[25px] lg:mx-auto lg:mt-[50px] lg:flex lg:h-[500px] xl:px-0"
    >
      <img
        class="absolute top-10 left-[600px] z-0 hidden h-[600px] w-[810px] origin-top-right lg:block"
        :src="$filters.media_path('/images/blue_gradient.svg')"
        alt=""
      />

      <div class="z-20 mx-auto mt-12 justify-center gap-1 py-0 lg:flex">
        <div class="mt-0 text-center lg:max-w-[500px] lg:text-left">
          <div
            id="text_video_hosting"
            class="text-xs font-extrabold text-[#2D8CFF]"
          >
            VIDEO HOSTING
          </div>

          <h2
            class="top-full mt-[20px] px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
          >
            Upload and Host Your Course Videos in one place.
          </h2>

          <hr
            class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#2D8CFF] lg:ml-0"
          />

          <p
            class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
          >
            Funnel all your customers to one login for all the different courses
            you sell. Get cross-pollination sales from customers that unlock
            your products with one click.
          </p>
        </div>

        <div class="z-30 mt-[25px] xl:mt-0">
          <img
            loading="lazy"
            class="lg:w-[650px]"
            :src="
              $filters.media_path(
                '/images/video-hosting-eay-uploads-for-your-course-pink.png'
              )
            "
            alt="Host videos without need for another video hosting service"
          />
        </div>
      </div>
    </section>

    <!-- ===================== ELEMENTS ===================== -->
    <section
      class="relative mt-[70px] w-full justify-center gap-2 px-[25px] lg:mx-auto lg:mt-[200px] lg:flex lg:h-[500px] lg:flex-row-reverse xl:px-0"
    >
      <img
        class="absolute top-0 right-[600px] z-0 h-[600px] w-[810px] origin-top-left overflow-hidden"
        :src="$filters.media_path('/images/green-diamond-dots-bg.svg')"
        alt=""
      />

      <div
        class="mt-12 text-center lg:mx-0 lg:mt-0 lg:max-w-[500px] lg:px-[30px] lg:text-left"
      >
        <div
          id="text_elements"
          class="text-xs font-extrabold text-[#27AE60] lg:left-0"
        >
          ELEMENTS
        </div>

        <h2
          class="top-full px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] md:mt-[20px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
        >
          Customize each lesson with PDFs, MP3s, and buttons.
        </h2>

        <div class="">
          <hr
            class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#27AE60] lg:ml-0"
          />
        </div>

        <p
          class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
        >
          Fast-track member and student results by adding a variety of content
          to each lesson. Make each module and lesson more valuable to dominate
          any niche.
        </p>
      </div>

      <div
        class="mx-auto mt-12 justify-center gap-1 py-0 md:z-10 lg:mt-0 lg:px-0"
      >
        <img
          loading="lazy"
          class="lg:w-[675px]"
          :src="
            $filters.media_path(
              '/images/online-course-platform-lesson-with-mp3-uploads.png'
            )
          "
          alt="Video upload module to add titles, descriptions, and thumbnails."
        />
      </div>
    </section>

    <!-- ===================== CUSTOMIZE ===================== -->
    <section
      class="relative mt-[70px] w-full justify-center gap-2 px-[25px] lg:mx-auto lg:mt-[250px] lg:flex lg:h-[550px] xl:px-0"
    >
      <img
        class="absolute top-10 left-[600px] z-0 hidden h-[600px] w-[810px] origin-top-right lg:block"
        :src="$filters.media_path('/images/platforma_turqoise_diamond_bg.svg')"
        alt=""
      />

      <div class="z-20 mx-auto mt-12 justify-center gap-1 py-0 lg:flex">
        <div class="mt-0 text-center lg:max-w-[500px] lg:text-left">
          <div
            id="text_automation"
            class="text-xs font-extrabold text-[#49CCF9]"
          >
            CUSTOMIZE
          </div>

          <h2
            class="top-full mt-[20px] px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
          >
            Drip feed modules by day, date, or progress.
          </h2>

          <hr
            class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#49CCF9] lg:ml-0"
          />

          <p
            class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
          >
            Design complex courses and programs by throttling member progress.
            Set Modules to remain locked until a specified time, or based on how
            much content a user consumed or comprehends. Prefect way to
            guarantee results, and avoid scammers consuming knowledge and
            refunding.
          </p>
        </div>

        <div class="z-30 mt-[25px] lg:mt-0">
          <img
            loading="lazy"
            class="lg:w-[625px]"
            :src="
              $filters.media_path(
                '/images/drip-feed-lessons-course-tracking.png'
              )
            "
            alt="Platforma video channel video scheduler."
          />
        </div>
      </div>
    </section>
  </div>

  <!--  FEATURES TABS START -->

  <FeaturesTabs />

  <!--  ICON BUTTON TABS START -->

  <IconButtonTabs />

  <!-- BACKGROUND TABS START -->

  <section class="w-full mx-auto mt-[100px]">
    <TabsBackgroundChange />
  </section>

  <!-- ============= EMAIL INTEGRATIONS ============= -->

  <EmailIntegrationsSlider />

  <!-- ===================== CTA BLOCK ===================== -->
  <CtaBlock
    cta-headline="Start using Platforma Courses 
and make more every month."
  />
</template>

<!-- CONTENT END -->

<script>
import DefaultLayout from '../Layouts/DefaultLayout.vue';
import ButtonComponent from '@shared/Components/ButtonComponent.vue';
import Axios from 'axios';
import EmailCaptureHeaderWidget from '../Components/EmailCaptureHeader.vue';
import EmailIntegrationsSlider from '../Components/EmailIntegrationsSlider.vue';
import FeaturesTabs from '../Components/FeaturesTabs10Max.vue';
import IconButtonTabs from '../Components/IconButtonTabs.vue';
import TabsBackgroundChange from '../Components/TabsBackgroundChange.vue';
import CtaBlock from '../Components/CtaBlockFooterComponent.vue';

export default {
  layout: DefaultLayout,
  name: 'pink-tabs',
  data() {
    return {
      openTab: 1
    };
  },
  methods: {
    toggleTabs: function (tabNumber) {
      this.openTab = tabNumber;
    }
  },

  components: {
    ButtonComponent,
    EmailCaptureHeaderWidget,
    EmailIntegrationsSlider,
    FeaturesTabs,
    IconButtonTabs,
    TabsBackgroundChange,
    CtaBlock
  },

  computed: {
    backgroundImageStyleMain() {
      const imagePath = this.$filters.media_path(
        'images/platforma-header-background-eclipse.svg'
      );
      return {
        '--before-background-image': `url(${imagePath})`
      };
    }
  }
};
</script>

<style scoped>
.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  height: 1000px;
  width: 1700px;
  display: block;
  z-index: 0;
  transform: translateY(-42%) translateX(58%);
  background-repeat: no-repeat;
  background-image: var(--before-background-image);
}

.join-mobile {
  background-image: url(../media/images/two-dots.svg);
  background-size: contain;
}
</style>
